import { useCallback, useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import { Ltr, useTranslation } from '../i18n.js'
import { DataSurface } from './DataSurface.js'

/**
 * Photographs of a frame, per colour. §6.4 `CAT-004`, `OPS-209`.
 *
 * `CAT-004` `MUST`: "Frame images per variant, multiple angles. This is not cosmetic, it is the
 * difference between searching the system and walking to the display wall."
 *
 * ---
 *
 * **This screen does not use the write queue, and that is the one place in the counter where that
 * is true.** `decisions/0019`: a frame photograph is two to six megabytes and `OFF-001a`'s queue is
 * IndexedDB on a cheap Android tablet, so a shop photographing forty frames at three angles would
 * hold half a gigabyte of JPEG in browser storage. The bytes are a transfer, not a write, and the
 * catalogue row the API writes beside them is what every screen reads.
 *
 * The cost is honest and shown: an upload that fails is an upload the shopkeeper has to repeat,
 * because there is no queue holding it. So the button says what is happening and a failure names
 * the picture rather than the request.
 *
 * **The bytes are read in the browser and sent base64**, which is a third more traffic than
 * multipart and the reason it is acceptable here: a shop uploads a handful of images in a sitting,
 * not a stream, and a base64 body needs no multipart parser on a route that would otherwise be the
 * only one in the API that has one.
 *
 * **Images are fetched with the session's own credentials**, because the bucket is private: the API
 * serves the bytes behind `catalogue_view`, so a picture is readable by exactly the people who can
 * read the frame beside it. That is why these are object URLs built from a fetch rather than plain
 * `src` attributes.
 */

export interface FrameImageRow {
  readonly imageId: string
  readonly contentType: string
  readonly byteSize: string
  readonly angle: string | null
  readonly position: number
}

/** One frame colour on file, as the picker reads it. `CAT-001`. */
export interface CatalogueFrameRow {
  readonly frameVariantId: string
  readonly brand: string
  readonly model: string
  readonly colourCode: string
  readonly colourName: string | null
}

export interface FrameImagesProps {
  /** Appendix B's `Catalogue - edit`, owner and manager. */
  readonly canEdit: boolean
  readonly loadFrames: () => Promise<readonly CatalogueFrameRow[]>
  readonly loadImages: (frameVariantId: string) => Promise<readonly FrameImageRow[]>
  /** Fetches the bytes with the session's credentials and hands back something a browser can show. */
  readonly loadImageBytes: (frameVariantId: string, imageId: string) => Promise<Blob>
  readonly upload: (
    frameVariantId: string,
    body: {
      readonly base64: string
      readonly contentType: string
      readonly angle?: string
      readonly position: number
    },
  ) => Promise<void>
  readonly remove: (frameVariantId: string, imageId: string) => Promise<void>
}

/**
 * The bytes of a chosen file, base64, with the `data:` prefix stripped.
 *
 * **`FileReader` and not `file.arrayBuffer()`**, which is the obvious modern form and the wrong one
 * here. `Blob.arrayBuffer` is Chrome 76 and later, and §13's hardware is cheap Android tablets
 * whose WebView may be older than that; `FileReader.readAsDataURL` has been universal for a decade.
 * A test found it, because jsdom has no `arrayBuffer` either, which is the same gap showing up
 * somewhere cheaper than a shop counter.
 *
 * It also avoids a chunked `btoa` loop: `String.fromCharCode(...bytes)` on a six megabyte array is
 * a spread of six million arguments and blows the call stack.
 */
const readAsBase64 = async (file: File): Promise<string> =>
  await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('unreadable'))
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : ''
      // `data:image/png;base64,AAAA` -> `AAAA`. The comma is the only separator in the grammar.
      const comma = result.indexOf(',')
      resolve(comma === -1 ? '' : result.slice(comma + 1))
    }
    reader.readAsDataURL(file)
  })

export function FrameImages({
  canEdit,
  loadFrames,
  loadImages,
  loadImageBytes,
  upload,
  remove,
}: FrameImagesProps): JSX.Element {
  const { t } = useTranslation()
  // Null is "not read yet", which is a different claim from "none": both lists used to start empty
  // and swallow a failed read into empty, so a broken connection read as a shop with no frames.
  const [frames, setFrames] = useState<readonly CatalogueFrameRow[] | null>(null)
  const [framesError, setFramesError] = useState<string | null>(null)
  const [framesAttempt, setFramesAttempt] = useState(0)
  const [frameVariantId, setFrameVariantId] = useState('')
  const [images, setImages] = useState<readonly FrameImageRow[] | null>(null)
  /** Separate from `error`, which belongs to uploads and removes and must not replace the list. */
  const [listError, setListError] = useState<string | null>(null)
  const [previews, setPreviews] = useState<Readonly<Record<string, string>>>({})
  /**
   * True once the thumbnail pass for the current list has finished. Without it every open would
   * flash "some pictures could not be shown" while the thumbnails were still on their way.
   */
  const [previewsDone, setPreviewsDone] = useState(false)
  const [angle, setAngle] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    let cancelled = false
    void loadFrames()
      .then((loaded) => {
        if (cancelled) return
        setFrames(loaded)
        setFramesError(null)
      })
      .catch(() => {
        if (!cancelled) setFramesError(t('frameImages.framesFailed'))
      })
    return () => {
      cancelled = true
    }
  }, [loadFrames, framesAttempt, t])

  const refresh = useCallback(async () => {
    if (frameVariantId === '') {
      setImages(null)
      return
    }
    try {
      setImages(await loadImages(frameVariantId))
      setListError(null)
    } catch {
      // Said, and the upload control stays: it sits outside the surface, because a shop whose list
      // failed to read is still a shop that needs to add a picture.
      setListError(t('frameImages.listFailed'))
    }
  }, [frameVariantId, loadImages, t])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    let cancelled = false
    const urls: string[] = []
    setPreviewsDone(false)
    if (images === null) return
    void (async () => {
      const next: Record<string, string> = {}
      for (const image of images) {
        try {
          const blob = await loadImageBytes(frameVariantId, image.imageId)
          const url = URL.createObjectURL(blob)
          urls.push(url)
          next[image.imageId] = url
        } catch {
          // One unreadable picture is one missing thumbnail, not a broken screen.
        }
      }
      if (!cancelled) {
        setPreviews(next)
        setPreviewsDone(true)
      }
    })()
    return () => {
      cancelled = true
      // Revoked on the way out, or a shop browsing twenty frames leaks twenty blobs a frame.
      for (const url of urls) URL.revokeObjectURL(url)
    }
  }, [frameVariantId, images, loadImageBytes])

  // Counted only after the thumbnail pass, and only for pictures that have no thumbnail. A missing
  // thumbnail is the one partial failure on this screen: the rows arrived and some bytes did not.
  const missing =
    previewsDone && images !== null
      ? images.filter((i) => previews[i.imageId] === undefined).length
      : 0

  const choose = useCallback(
    async (file: File | undefined) => {
      if (file === undefined) return
      setError(null)
      setDone(null)
      setBusy(true)
      try {
        const base64 = await readAsBase64(file)
        // The next free position, so a shopkeeper never types a number and never meets the clash
        // the server would answer with.
        const position = (images ?? []).reduce((highest, i) => Math.max(highest, i.position), 0) + 1
        await upload(frameVariantId, {
          base64,
          contentType: file.type,
          ...(angle.trim() === '' ? {} : { angle: angle.trim() }),
          position,
        })
        setDone(t('frameImages.saved'))
        setAngle('')
        if (fileInput.current !== null) fileInput.current.value = ''
        await refresh()
      } catch (uploadError) {
        /**
         * Named, because there is no queue holding this: an upload that fails is one the shopkeeper
         * has to repeat, and "something went wrong" would leave them unsure whether to.
         */
        setError(uploadError instanceof Error ? uploadError.message : t('frameImages.uploadFailed'))
      } finally {
        setBusy(false)
      }
    },
    [angle, frameVariantId, images, refresh, t, upload],
  )

  const drop = useCallback(
    async (imageId: string) => {
      setError(null)
      setDone(null)
      setBusy(true)
      try {
        await remove(frameVariantId, imageId)
        await refresh()
      } catch (removeError) {
        setError(removeError instanceof Error ? removeError.message : t('frameImages.removeFailed'))
      } finally {
        setBusy(false)
      }
    },
    [frameVariantId, refresh, remove, t],
  )

  return (
    <section className="lm-setup-panel" aria-labelledby="frame-images-heading">
      <h2 id="frame-images-heading" className="lm-setup-panel__heading">
        {t('frameImages.heading')}
      </h2>
      <p className="lm-setup-panel__effects">{t('frameImages.means')}</p>

      <DataSurface
        state={
          framesError !== null
            ? {
                kind: 'error',
                message: framesError,
                onRetry: () => {
                  setFramesError(null)
                  setFrames(null)
                  setFramesAttempt((n) => n + 1)
                },
              }
            : frames === null
              ? { kind: 'loading' }
              : frames.length === 0
                ? { kind: 'empty' }
                : { kind: 'ready' }
        }
        shape="lines"
        rows={1}
        what={t('frameImages.framesWhat')}
        emptyMessage={t('frameImages.noFrames')}
      >
        <label className="lm-field">
          <span className="lm-field__label">{t('frameImages.frame')}</span>
          <select
            className="lm-input"
            value={frameVariantId}
            onChange={(e) => {
              setFrameVariantId(e.currentTarget.value)
              // The previous frame's pictures must not sit under the new frame while it is read.
              setImages(null)
              setListError(null)
              setPreviews({})
            }}
          >
            <option value="">{t('frameImages.pickAFrame')}</option>
            {(frames ?? []).map((f) => (
              <option key={f.frameVariantId} value={f.frameVariantId}>
                {`${f.brand} ${f.model} ${f.colourName ?? f.colourCode}`}
              </option>
            ))}
          </select>
        </label>
      </DataSurface>

      {frameVariantId === '' ? null : (
        <DataSurface
          state={
            listError !== null
              ? {
                  kind: 'error',
                  message: listError,
                  onRetry: () => {
                    setListError(null)
                    setImages(null)
                    void refresh()
                  },
                }
              : images === null
                ? { kind: 'loading' }
                : images.length === 0
                  ? { kind: 'empty' }
                  : missing > 0
                    ? {
                        kind: 'partial',
                        missing: t('frameImages.missingCount', { count: missing }),
                      }
                    : { kind: 'ready' }
          }
          shape="rows"
          rows={2}
          what={t('frameImages.what')}
          emptyMessage={canEdit ? t('frameImages.noneYetCanEdit') : t('frameImages.noneYet')}
        >
          <ul className="lm-location-list">
            {(images ?? []).map((image) => (
              <li key={image.imageId} className="lm-location-row">
                {previews[image.imageId] === undefined ? (
                  <span className="lm-location-row__meta">{t('frameImages.loading')}</span>
                ) : (
                  <img
                    className="lm-frame-image"
                    src={previews[image.imageId]}
                    alt={
                      image.angle === null
                        ? t('frameImages.altNoAngle', { n: image.position })
                        : t('frameImages.alt', { angle: image.angle })
                    }
                  />
                )}
                <span className="lm-location-row__name" dir="auto">
                  {image.angle === null
                    ? t('frameImages.positionOnly', { n: image.position })
                    : image.angle}
                </span>
                {/* `OPS-209` makes usage visible, and per picture is where a shop can act on it. */}
                <span className="lm-location-row__meta" dir="ltr">
                  <Ltr>{`${String(Math.ceil(Number(image.byteSize) / 1024))} kB`}</Ltr>
                </span>
                {canEdit ? (
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    disabled={busy}
                    onClick={() => {
                      void drop(image.imageId)
                    }}
                  >
                    {t('frameImages.remove')}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </DataSurface>
      )}

      {canEdit && frameVariantId !== '' ? (
        <>
          <label className="lm-field">
            <span className="lm-field__label">{t('frameImages.angle')}</span>
            {/* Free text, because Appendix E has no vocabulary for an image angle and inventing a
                five-value chooser here would put one in the database the document never agreed to. */}
            <input
              className="lm-input"
              dir="auto"
              value={angle}
              autoComplete="off"
              placeholder={t('frameImages.anglePlaceholder')}
              onChange={(e) => setAngle(e.currentTarget.value)}
            />
          </label>

          <label className="lm-field">
            <span className="lm-field__label">{t('frameImages.choose')}</span>
            {/* `capture` is deliberately absent: a shopkeeper photographing a delivery may be using
                a tablet camera or a file from a supplier's email, and forcing the camera would make
                the second impossible. */}
            <input
              ref={fileInput}
              className="lm-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              disabled={busy}
              onChange={(e) => {
                void choose(e.currentTarget.files?.[0])
              }}
            />
          </label>

          {busy ? <p className="lm-hint">{t('frameImages.uploading')}</p> : null}
        </>
      ) : null}

      {error === null ? null : (
        <p className="lm-error" role="alert">
          {error}
        </p>
      )}
      {done === null ? null : (
        <p className="lm-hint" role="status">
          {done}
        </p>
      )}
    </section>
  )
}
